import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { FundFitToolUsageByOrganization } from '@/types/service/internalAnalytics';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { CHART_COLORS, formatCurrency, formatNumber } from '@/utils/saasDashboardHelpers';

interface FundFitToolUsageByOrgChartProps {
  data: FundFitToolUsageByOrganization[];
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: {
      name: string;
      cost: number;
      calls: number;
      users: number;
    };
  }>;
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div className='bg-white p-3 border rounded shadow-lg'>
      <p className='font-medium text-sm mb-2'>{label}</p>
      <p className='text-sm'>Cost: {formatCurrency(data.cost)}</p>
      <p className='text-sm'>Tool Calls: {formatNumber(data.calls)}</p>
      <p className='text-sm'>Users: {data.users}</p>
    </div>
  );
};

const FundFitToolUsageByOrgChart = ({ data }: FundFitToolUsageByOrgChartProps) => {
  if (data.length === 0) {
    return (
      <Card className='h-full'>
        <CardHeader>
          <CardTitle className='text-lg'>Tool Cost by Organization</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='h-[300px] flex items-center justify-center text-muted-foreground'>
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data
    .map((item) => ({
      name: item.organization,
      cost: parseFloat(item.total_tool_usd),
      calls: item.tool_count,
      users: item.unique_users,
    }))
    .slice(0, 15);

  const chartHeight = Math.max(300, chartData.length * 50);
  const maxLabelLength = Math.max(...chartData.map((item) => item.name.length));
  const yAxisWidth = Math.min(Math.max(maxLabelLength * 7, 120), 250);

  return (
    <Card className='h-full'>
      <CardHeader>
        <CardTitle className='text-lg'>Tool Cost by Organization</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width='100%' height={chartHeight}>
          <BarChart
            data={chartData}
            layout='vertical'
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray='3 3' horizontal={true} vertical={false} />
            <XAxis type='number' tickFormatter={(value) => formatCurrency(value)} />
            <YAxis type='category' dataKey='name' width={yAxisWidth} tick={{ fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey='cost'
              name='Cost (USD)'
              fill={CHART_COLORS.pie[2]}
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default FundFitToolUsageByOrgChart;
